/**
 * La pile du SAV : ce qui est remonté, ce qu'on en fait, et ce qui est immobilisé.
 *
 * *Décision de Chris, 21/09/2026 : « toutes les remontées d'incident doivent être vues par
 * assistantes et responsable SAV ; en réalité c'est l'assistante qui prendra en compte mais le
 * responsable SAV verra aussi. »*
 *
 * ## Un écran, trois lecteurs
 *
 * Le serveur borne déjà ce que chaque compte voit — tout pour le SAV, ses équipes pour un
 * encadrant, les siennes pour un déclarant. L'écran ne refait donc **aucun filtre** : deux
 * endroits qui filtrent finissent par ne plus filtrer pareil, et c'est un écran d'alerte, celui
 * où un trou se lit « tout va bien ».
 *
 * Ce qu'il fait, en revanche, c'est **dire ce qu'il est** : quand la pile n'est désignée à
 * personne, il l'écrit et renvoie aux membres. Une fonction qui arrive muette est le défaut que ce
 * produit a déjà payé deux fois (la case jamais cochée du 02/09, l'écran d'alerte vide du 16/08).
 *
 * ## La date de remise à disposition est le cœur de l'écran
 *
 * *« La date qui compte n'est pas celle de l'immobilisation, c'est la date prévisionnelle de
 * remise à disposition. Même approximative, elle doit être saisie, en se trompant plutôt vers le
 * haut. »* Elle est donc obligatoire à la qualification, elle se corrige ensuite d'un champ, et la
 * demande de l'exploitation — « j'en ai besoin le 19 » — s'affiche juste à côté d'elle, parce que
 * c'est la comparaison des deux qui fait décider.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import {
  chargerIncidents,
  deplacerRemise,
  poserLeBesoin,
  qualifierIncident,
  solderImmobilisation,
} from "./api";
import type { Incident } from "./api";

const ISSUES: readonly { readonly code: "sans_suite" | "atelier" | "immobilisation"; readonly libelle: string; readonly aide: string }[] =
  [
    {
      code: "sans_suite",
      libelle: "Classer sans suite",
      aide: "Rien à faire — mais la remontée reste, et se compte.",
    },
    {
      code: "atelier",
      libelle: "Réparation atelier",
      aide: "À réparer, la machine reste disponible au planning.",
    },
    {
      code: "immobilisation",
      libelle: "Immobiliser",
      aide: "La machine sort du planning jusqu'à sa remise à disposition.",
    },
  ];

/** Le jour, au format des champs `date`. */
function aujourdHui(): string {
  return new Date().toISOString().slice(0, 10);
}

export function Incidents(): JSX.Element {
  const [incidents, setIncidents] = useState<readonly Incident[]>([]);
  const [peutQualifier, setPeutQualifier] = useState(false);
  const [pileDesignee, setPileDesignee] = useState(true);
  const [ouvertesSeules, setOuvertesSeules] = useState(true);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  /** La remontée dépliée, et la décision en cours de saisie sur elle. */
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [issue, setIssue] = useState<"sans_suite" | "atelier" | "immobilisation">("atelier");
  const [motif, setMotif] = useState("");
  const [remisePrevue, setRemisePrevue] = useState("");
  const [travail, setTravail] = useState(false);

  const recharger = useCallback((): void => {
    setChargement(true);
    chargerIncidents(ouvertesSeules)
      .then((r) => {
        setIncidents(r.incidents);
        setPeutQualifier(r.peutQualifier);
        setPileDesignee(r.pileDesignee);
        setErreur(null);
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setChargement(false);
      });
  }, [ouvertesSeules]);

  useEffect(recharger, [recharger]);

  const deplier = (incident: Incident): void => {
    if (ouvert === incident.id) {
      setOuvert(null);
      return;
    }
    setOuvert(incident.id);
    /*
      L'issue proposée vient du TYPE, et se corrige.

      Une panne ou un accident immobilisent d'après le tableau de Chris ; une casse est « à
      qualifier ». La proposer épargne cinq gestes par jour ; la poser toute seule mettrait au
      planning une machine qui roule — d'où une proposition et non une décision.
    */
    setIssue(incident.effetMateriel === "immediate" ? "immobilisation" : "atelier");
    setMotif("");
    setRemisePrevue("");
  };

  const qualifier = (incident: Incident): void => {
    setTravail(true);
    setErreur(null);
    qualifierIncident(incident.id, {
      issue,
      motif: motif.trim() === "" ? undefined : motif.trim(),
      debut: aujourdHui(),
      remisePrevue: issue === "immobilisation" ? remisePrevue : undefined,
    })
      .then(() => {
        setOuvert(null);
        recharger();
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setTravail(false);
      });
  };

  const agir = (action: Promise<unknown>): void => {
    setTravail(true);
    setErreur(null);
    action
      .then(() => {
        recharger();
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setTravail(false);
      });
  };

  return (
    <section className="incidents">
      <h1>Remontées d'incident</h1>

      {/*
        Ce que l'écran dit de lui-même, avant toute donnée.

        Une pile que personne ne reçoit accepte quand même les déclarations : mieux vaut un
        incident tracé que personne n'a encore ouvert qu'un incident jamais saisi. Mais il faut
        que ce soit DIT — sans quoi on croit que le circuit fonctionne.
      */}
      {!pileDesignee && (
        <p className="alerte">
          Aucune assistante ni responsable SAV n'est désigné : les remontées s'enregistrent, mais
          personne ne les reçoit. Ils se désignent depuis l'écran des membres.
        </p>
      )}
      {!peutQualifier && pileDesignee && (
        <p className="note">
          Vous voyez ici ce que vous avez déclaré et ce que vos équipes remontent. La qualification
          appartient aux assistantes et au responsable SAV.
        </p>
      )}

      <label className="incidents__filtre">
        <input
          type="checkbox"
          checked={ouvertesSeules}
          onChange={(e) => {
            setOuvertesSeules(e.target.checked);
          }}
        />
        <span>N'afficher que ce qui attend une qualification</span>
      </label>

      {erreur !== null && <p className="alerte">{erreur}</p>}
      {chargement && <p className="note">Chargement…</p>}

      {!chargement && incidents.length === 0 && (
        <p className="note">
          {ouvertesSeules
            ? "Rien n'attend de qualification."
            : "Aucune remontée d'incident pour le moment."}
        </p>
      )}

      <table className="incidents__table">
        <thead>
          <tr>
            <th>N°</th>
            <th>Machine</th>
            <th>Type</th>
            <th>Description</th>
            <th>Où</th>
            <th>Déclaré par</th>
            <th>État</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {incidents.map((i) => (
            <tr key={i.id} className={i.statut === "declaree" ? "incidents__ouverte" : undefined}>
              <td data-colonne="N°">{i.numero}</td>
              <td data-colonne="Machine">
                <code>{i.materielCode}</code>
                {i.materielDescription === null ? null : (
                  <>
                    <br />
                    <span className="note">{i.materielDescription}</span>
                  </>
                )}
              </td>
              <td data-colonne="Type">
                {i.typeLibelle ?? "—"}
                {i.immobiliseeConstatee === true && (
                  <>
                    <br />
                    <span className="note">à l'arrêt sur place</span>
                  </>
                )}
              </td>
              <td data-colonne="Description">
                {i.constat}
                {i.photos > 0 && (
                  <>
                    <br />
                    {/*
                      Les photos du dégât. Une adresse par photo, servie par le serveur — le
                      navigateur ne connaît aucun chemin de fichier.
                    */}
                    <span className="note">
                      {i.photos === 1 ? "1 photo" : `${String(i.photos)} photos`}
                    </span>
                  </>
                )}
              </td>
              <td data-colonne="Où">{i.horsChantier ? "Atelier" : (i.chantierApo ?? "—")}</td>
              <td data-colonne="Déclaré par">
                {i.saisiParLibelle}
                {i.nPlus1Resolu !== "fiche" && (
                  <>
                    <br />
                    <span className="alerte">copie N+1 non aboutie</span>
                  </>
                )}
              </td>
              <td data-colonne="État">
                {i.statut === "declaree" ? (
                  <strong>À qualifier</strong>
                ) : i.immobilisation !== null && !i.immobilisation.soldee ? (
                  <>
                    Immobilisée jusqu'au{" "}
                    <strong>{i.immobilisation.remisePrevue ?? "—"}</strong>
                    {i.immobilisation.besoinLe !== null && (
                      <>
                        <br />
                        <span className="alerte">
                          demandée pour le {i.immobilisation.besoinLe}
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  (ISSUES.find((x) => x.code === i.issue)?.libelle ?? i.issue ?? "—")
                )}
              </td>
              <td className="incidents__action">
                <button
                  type="button"
                  className="lien"
                  onClick={() => {
                    deplier(i);
                  }}
                >
                  {ouvert === i.id ? "Fermer" : "Ouvrir"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {incidents
        .filter((i) => i.id === ouvert)
        .map((i) => (
          <div key={i.id} className="fiche incidents__fiche">
            <h2>
              Remontée n° {i.numero} — {i.materielCode}
            </h2>
            <p>{i.constat}</p>
            {i.photos > 0 && (
              <div className="incidents__photos">
                {/*
                  Chaque photo est demandée par son identifiant : le serveur relit le nom en base
                  avant de servir le fichier, ce qui empêche une base modifiée à la main de faire
                  servir n'importe quel fichier de la machine.
                */}
                <p className="note">
                  {i.photos === 1 ? "1 photo jointe" : `${String(i.photos)} photos jointes`} —
                  visibles depuis la remontée.
                </p>
              </div>
            )}

            {i.statut === "declaree" && peutQualifier && (
              <>
                <fieldset className="or__gravite">
                  <legend>Qu'en fait-on ?</legend>
                  {ISSUES.map((x) => (
                    <label key={x.code}>
                      <input
                        type="radio"
                        name="issue"
                        checked={issue === x.code}
                        onChange={() => {
                          setIssue(x.code);
                        }}
                      />
                      <strong>{x.libelle}</strong>
                      <span className="note">{x.aide}</span>
                    </label>
                  ))}
                </fieldset>

                {issue === "immobilisation" && (
                  <label>
                    <span>Remise à disposition prévue</span>
                    <input
                      type="date"
                      value={remisePrevue}
                      min={aujourdHui()}
                      onChange={(e) => {
                        setRemisePrevue(e.target.value);
                      }}
                    />
                    <span className="note">
                      Même approximative : mieux vaut annoncer dix jours et rendre au bout de cinq
                      que l'inverse.
                    </span>
                  </label>
                )}

                <label>
                  <span>{issue === "sans_suite" ? "Pourquoi (obligatoire)" : "Commentaire"}</span>
                  <textarea
                    rows={2}
                    value={motif}
                    onChange={(e) => {
                      setMotif(e.target.value);
                    }}
                  />
                </label>

                <div className="fiche__actions">
                  <button
                    type="button"
                    disabled={
                      travail ||
                      (issue === "sans_suite" && motif.trim() === "") ||
                      (issue === "immobilisation" && remisePrevue === "")
                    }
                    onClick={() => {
                      qualifier(i);
                    }}
                  >
                    {travail ? "Enregistrement…" : "Qualifier"}
                  </button>
                </div>
              </>
            )}

            {i.immobilisation !== null && !i.immobilisation.soldee && (
              <div className="incidents__immobilisation">
                <h3>Immobilisation</h3>
                <p>
                  Depuis le {i.immobilisation.debut ?? "—"}, remise prévue le{" "}
                  <strong>{i.immobilisation.remisePrevue ?? "—"}</strong>.
                </p>

                {i.immobilisation.besoinLe !== null && (
                  <p className="alerte">
                    L'exploitation la demande pour le {i.immobilisation.besoinLe}
                    {i.immobilisation.besoinMotif === null
                      ? "."
                      : ` — ${i.immobilisation.besoinMotif}`}
                  </p>
                )}

                {peutQualifier && (
                  <div className="fiche__actions">
                    <label>
                      <span>Déplacer la remise à disposition</span>
                      <input
                        type="date"
                        defaultValue={i.immobilisation.remisePrevue ?? ""}
                        onChange={(e) => {
                          if (e.target.value === "") return;
                          agir(deplacerRemise(i.immobilisation?.id ?? "", e.target.value));
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      disabled={travail}
                      onClick={() => {
                        agir(solderImmobilisation(i.immobilisation?.id ?? ""));
                      }}
                    >
                      La machine est revenue
                    </button>
                  </div>
                )}

                {/*
                  La question de l'exploitation, posée dans l'outil plutôt qu'au téléphone.

                  Ouverte à tout compte, volontairement : c'est l'exploitation qui constate le
                  conflit, et lui demander un droit de plus serait la renvoyer au téléphone.
                */}
                <label>
                  <span>J'ai besoin de cette machine le</span>
                  <input
                    type="date"
                    defaultValue={i.immobilisation.besoinLe ?? ""}
                    onChange={(e) => {
                      agir(poserLeBesoin(i.immobilisation?.id ?? "", e.target.value));
                    }}
                  />
                </label>
              </div>
            )}

            {i.statut !== "declaree" && i.qualifieLe !== null && (
              <p className="note">
                Qualifié par {i.qualifieParLibelle ?? "—"} le{" "}
                {new Date(i.qualifieLe).toLocaleString("fr-FR")}
                {i.qualificationMotif === null ? "" : ` — ${i.qualificationMotif}`}
              </p>
            )}
          </div>
        ))}
    </section>
  );
}
